import * as React from 'react';
import { datadogRum } from '@datadog/browser-rum-slim';
import type { EmotionCache } from '@emotion/react';
import type { AppProps } from 'next/app';

import type { AppPageProps } from '../api/serverSession/session.types';
import { isAuthenticatedPageProps } from '../api/serverSession/session.types';
import { useSessionTimer } from '../hooks/useSessionTimer';
import { AppProviders } from '../providers/appProviders';
import { AuthenticatedProviders } from '../providers/authenticatedProviders';
import { UnauthenticatedProviders } from '../providers/unauthenticatedProviders';
import { createEmotionCache } from '../utils/emotion';
import { injectDatadogRum } from '../utils/rum';
import { isServer } from '../utils/window';

import '../styles/global.scss';
import '@seccl/fonts/assets/brown.css';
import '@seccl/forms/forms.css';
import '@seccl/ui/ui.css';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

const isDatadogRumCandidate = (host?: string) => {
  if (!host) {
    return false;
  }

  const exclusions = ['local.seccl.tech', 'localhost'];
  const result = !exclusions.some(item => host.includes(item));
  return result;
};

export interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  pageProps: AppPageProps;
}

/**
 * Custom Next.js App component
 * This component is used to initialize pages with a persistent layout or shared styles.
 * @param {AppProps} props - Props for the custom Next.js App component
 * @returns {React.ReactElement} JSX element for the custom App component
 */
export default function MyApp({ Component, emotionCache = clientSideEmotionCache, pageProps }: MyAppProps) {
  const initRef = React.useRef(false);
  const userId = isAuthenticatedPageProps(pageProps) ? pageProps.session.userId : undefined;
  const isLoggedIn = !!userId;
  const isPlaywright = process.env.NEXT_PUBLIC_IS_PLAYWRIGHT === 'true';

  const ddRumInitRef = React.useRef(false);
  useSessionTimer(initRef, isLoggedIn, !!isPlaywright);

  if (isServer && process.env.NODE_ENV === 'development' && process.env.MOCKING_ENABLED === 'true') {
    import('../utils/mocks');
  }

  React.useEffect(() => {
    if (isDatadogRumCandidate(window?.location?.host) && !ddRumInitRef.current) {
      ddRumInitRef.current = true;
      injectDatadogRum();
    }

    if (ddRumInitRef && userId && !datadogRum.getGlobalContext()?.userId) {
      datadogRum.setGlobalContext({ userId });
    }
  }, [userId]);

  const page = <Component {...pageProps} />;

  return (
    <AppProviders pageProps={pageProps} emotionCache={emotionCache}>
      {isAuthenticatedPageProps(pageProps) ? (
        <AuthenticatedProviders pageProps={pageProps}>{page}</AuthenticatedProviders>
      ) : (
        <UnauthenticatedProviders>{page}</UnauthenticatedProviders>
      )}
    </AppProviders>
  );
}
